import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type {
  ShellNavigateGuard,
  ShellNavigationPort,
} from '../../../../app-context/domain/ports/ShellNavigationPort.ts';

export type RouteHandler = () => void | Promise<void>;

export interface Route {
  path: string;
  handler: RouteHandler;
}

export type NewCorrelationId = () => string;

export class Router implements ShellNavigationPort {
  private readonly routes: Route[] = [];
  private readonly navigateGuards: ShellNavigateGuard[] = [];
  private currentPath: string = '';
  private started = false;
  /** Constructs this instance. */


  constructor(
    private readonly telemetry: TelemetryPort,
    private readonly newCorrelationId: NewCorrelationId
  ) {}
  /** Add route. */


  addRoute(path: string, handler: RouteHandler): void {
    this.routes.push({ path, handler });
  }
  /** Add navigate guard. */


  addNavigateGuard(guard: ShellNavigateGuard): () => void {
    this.navigateGuards.push(guard);
    return () => {
      const i = this.navigateGuards.indexOf(guard);
      if (i >= 0) this.navigateGuards.splice(i, 1);
    };
  }
  /** Navigate. */


  navigate(path: string): void {
    const target = normalizeInternalNavigateTarget(path);
    const fromPath = this.currentPath || globalThis.location.pathname;
    if (target.pathname !== fromPath && !this.runNavigateGuards(target.pathname, fromPath)) return;
    const href = `${target.pathname}${target.search}${target.hash}`;
    globalThis.history.pushState({}, '', href);
    void this.handleRoute();
    globalThis.dispatchEvent(new CustomEvent('navigate', { detail: { path: target.pathname } }));
  }
  /** Start (idempotent: safe to call again after in-app login resume). */
  start(): void {
    if (this.started) {
      void this.handleRoute();
      return;
    }
    this.started = true;
    void this.handleRoute();
    globalThis.addEventListener('popstate', () => {
      const path = globalThis.location.pathname;
      const fromPath = this.currentPath;
      if (path !== fromPath && !this.runNavigateGuards(path, fromPath)) {
        globalThis.history.pushState({}, '', fromPath || path);
        return;
      }
      void this.handleRoute();
    });
    document.addEventListener('click', (e) => {
      try {
        const target = (e.target as HTMLElement).closest('a');
        if (!target?.href?.startsWith(globalThis.location.origin)) return;
        e.preventDefault();
        let path: string;
        try {
          const url = new URL(target.href);
          path = `${url.pathname}${url.search}${url.hash}`;
        } catch (err) {
          this.telemetry.logError('router: URL interna inválida', {
            correlationId: this.newCorrelationId(),
            source: 'router.linkClick',
            href: target.href,
            error: err,
          });
          return;
        }
        this.navigate(path);
      } catch (err) {
        this.telemetry.logError(err instanceof Error ? err.message : String(err), {
          correlationId: this.newCorrelationId(),
          source: 'router.linkClick.fatal',
          error: err,
        });
      }
    });
  }
  /** Runs registered navigate guards (newest first). */
  private runNavigateGuards(path: string, fromPath: string): boolean {
    if (path === fromPath) return true;
    for (let i = this.navigateGuards.length - 1; i >= 0; i--) {
      try {
        if (!this.navigateGuards[i](path, fromPath)) return false;
      } catch (err) {
        this.telemetry.logError(err instanceof Error ? err.message : 'navigate guard', {
          correlationId: this.newCorrelationId(),
          source: 'router.navigateGuard',
          path,
          fromPath,
          error: err,
        });
        return false;
      }
    }
    return true;
  }

  /** Map shell document URL (/index.html) to the default SPA route after hard reload. */
  private resolveShellPathname(): string {
    const path = globalThis.location.pathname;
    if (path !== '/index.html') return path;
    globalThis.history.replaceState({}, '', '/');
    return '/';
  }

  /** Match route. */


  private matchRoute(path: string): Route | undefined {
    return this.routes.find(
      (r) => r.path === path || (r.path.endsWith('*') && path.startsWith(r.path.slice(0, -1)))
    );
  }
  /** Handles route. */


  private async handleRoute(): Promise<void> {
    try {
      const path = this.resolveShellPathname();
      this.currentPath = path;
      const route = this.matchRoute(path);

      if (route) {
        try {
          await route.handler();
        } catch (error) {
          try {
            this.telemetry.logError(error instanceof Error ? error : String(error), {
              correlationId: this.newCorrelationId(),
              source: 'router',
              path,
            });
          } catch {
            /* If telemetry fails, do not propagate (popstate / navigate must continue). */
          }
        }
      }
    } catch (e) {
      try {
        this.telemetry.logError(e instanceof Error ? e.message : String(e), {
          correlationId: this.newCorrelationId(),
          source: 'router.fatal',
        });
      } catch {
        void 0;
      }
    }
  }
  /** Returns current path. */


  getCurrentPath(): string {
    return this.currentPath;
  }
}

type InternalNavigateTarget = { pathname: string; search: string; hash: string };

function normalizeInternalNavigateTarget(path: string): InternalNavigateTarget {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) {
    return { pathname: trimmed, search: '', hash: '' };
  }
  try {
    const url = new URL(trimmed, globalThis.location.origin);
    return { pathname: url.pathname, search: url.search, hash: url.hash };
  } catch {
    return { pathname: trimmed.split('?')[0]?.split('#')[0] || '/', search: '', hash: '' };
  }
}
